import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as ChangeStatusIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS, REPACK_DASHBOARD_STATUSES, REPACK_STATUS_COLORS } from '../../config';
import { PRODUCTS, GRADES, BRANDS, PACK_SIZES } from '../../utils/DROPDOWN_OPTIONS';
import ChangeStatusModal from '../modals/ChangeStatusModal';
import ImportDataModal from '../modals/ImportDataModal';
import ExportButton from '../../utils/ExportButton';

// Helper function to get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// ✅ Function to format GRADE values - removes leading zeros and prevents date conversion
const formatGrade = (grade) => {
  if (!grade) return '';
  
  const gradeStr = String(grade).trim();
  
  // Handle Excel date conversions like "08-Jun" -> "8/12"
  const datePattern = /^(\d{1,2})-([A-Za-z]{3})$/;
  const dateMatch = gradeStr.match(datePattern);
  
  if (dateMatch) {
    const day = parseInt(dateMatch[1]);
    const monthAbbr = dateMatch[2].toLowerCase();
    
    // Month abbreviation to number mapping
    const monthMap = {
      'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4, 'may': 5, 'jun': 6,
      'jul': 7, 'aug': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12
    };
    
    const month = monthMap[monthAbbr];
    if (month) {
      // Convert back to grade format
      // Excel converts "6/8" to "08-Jun" (thinking it's month/day)
      // So we reverse it: "08-Jun" -> "6/8" (month=6, day=8)
      return `${month}/${day}`;
    }
  }
  
  // Handle grade patterns like "06/8", "21/25", "16/20"
  // Remove leading zeros from the first number
  const match = gradeStr.match(/^0*(\d+)\/(\d+)$/);
  
  if (match) {
    // Remove leading zero from first number, keep second number as is
    return `${match[1]}/${match[2]}`;
  }
  
  // If no match, return as is
  return gradeStr;
};

function RepackOutDashboard({ user }) {
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters - ✅ UPDATED: date_from and date_to instead of single date
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    product: '',
    grade: '',
    brand: '',
    pallet_id: '',
    cs1: '',
    cs2: '',
    status: '',
  });

  // Modal states
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  const [selectedRows, setSelectedRows] = useState([]);

  // Status counts for summary cards
  const [statusCounts, setStatusCounts] = useState({
    'REPACK-OUT': 0,
    'RE-PRINTING': 0,
    'RE-PROCESS': 0,
    'ONLINE MIX': 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        view: 'repack_out',
        ...filters,
      });

      const response = await axios.get(
        `${API_ENDPOINTS.INVENTORY}?${params}`,
        getAuthConfig()
      );

      if (response.data.success) {
        // Calculate total_kgs and total_pounds if they're 0 or missing
        const processedData = (response.data.data || []).map(item => {
          let total_kgs = Number(item.total_kgs) || 0;
          let total_pounds = Number(item.total_pounds) || 0;
          
          // If total_kgs is 0, calculate it
          if (total_kgs === 0) {
            const cartons = Number(item.cartons) || 0;
            const loose_bags = Number(item.loose_bags) || 0;
            const loose_kgs = Number(item.loose_kgs) || 0;
            const pack_size = item.pack_size || '';
            
            // Parse pack size to get weight per bag
            const packMatch = pack_size.match(/(\d+\.?\d*)\s*[Xx\/]\s*(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/i);
            if (packMatch) {
              const bagsPerCarton = parseFloat(packMatch[1]) || 0;
              const weightPerBag = parseFloat(packMatch[2]) || 0;
              const unit = (packMatch[3] || 'LB').toUpperCase();
              
              const totalBags = (bagsPerCarton * cartons) + loose_bags;
              let totalWeight = totalBags * weightPerBag;
              
              // Convert to KG if needed
              if (unit.includes('LB')) {
                totalWeight = totalWeight * 0.453592;
              } else if (unit.includes('OZ')) {
                totalWeight = totalWeight * 0.0283495;
              }
              
              total_kgs = totalWeight + loose_kgs;
            } else {
              total_kgs = loose_kgs;
            }
          }
          
          // Calculate pounds from kgs
          if (total_pounds === 0 && total_kgs > 0) {
            total_pounds = total_kgs * 2.20462;
          }
          
          // ✅ Format grade to remove leading zeros and fix any date conversions
          const formattedGrade = formatGrade(item.grade);
          
          return {
            ...item,
            grade: formattedGrade,
            total_kgs,
            total_pounds
          };
        });
        
        setData(processedData);
        setTotals(response.data.totals || {});
        
        // Calculate status counts
        const counts = {
          'REPACK-OUT': 0,
          'RE-PRINTING': 0,
          'RE-PROCESS': 0,
          'ONLINE MIX': 0
        };
        processedData.forEach(item => {
          if (counts.hasOwnProperty(item.status)) {
            counts[item.status]++;
          }
        });
        setStatusCounts(counts);
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Failed to fetch repack-out data:', err);
      setError('Failed to load repack-out inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      product: '',
      grade: '',
      brand: '',
      pallet_id: '',
      cs1: '',
      cs2: '',
      status: '',
    });
    setTimeout(() => {
      fetchData();
    }, 100);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_ENDPOINTS.INVENTORY}?id=${id}`,
        getAuthConfig()
      );

      if (response.data.success) {
        alert('Entry deleted successfully!');
        fetchData();
      }
    } catch (err) {
      console.error('Failed to delete entry:', err);
      alert('Failed to delete entry: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one row to delete');
      return;
    }

    if (!window.confirm(`⚠️ Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API_ENDPOINTS.BULK_DELETE,  // ✅ Uses config endpoint',
        { ids: selectedRows },
        { 
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true 
        }
      );

      if (response.data.success) {
        alert(`Successfully deleted ${response.data.deleted_count} record(s)`);
        setSelectedRows([]);
        fetchData();
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert('Failed to delete records: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map(row => row.id));
    }
  };

  // Calculate totals from filtered data
  const calculateFilteredTotals = (filteredData) => {
    return {
      cartons: filteredData.reduce((sum, item) => sum + (Number(item.cartons) || 0), 0),
      loose_bags: filteredData.reduce((sum, item) => sum + (Number(item.loose_bags) || 0), 0),
      loose_kgs: filteredData.reduce((sum, item) => sum + (Number(item.loose_kgs) || 0), 0),
      total_kgs: filteredData.reduce((sum, item) => sum + (Number(item.total_kgs) || 0), 0),
      total_pounds: filteredData.reduce((sum, item) => sum + (Number(item.total_pounds) || 0), 0),
    };
  };

  // ✅ NEW: Check if any filter is active (to show consolidated summary)
  const isAnyFilterActive = () => {
    return filters.status || filters.product || filters.grade || filters.brand || 
           filters.date_from || filters.date_to;
  };

  // ✅ NEW: Calculate consolidated summary grouped by Product, Grade, Pack Size, Brand
  const getConsolidatedSummary = (data) => {
    const summaryMap = {};
    
    data.forEach(item => {
      const key = `${item.product || 'N/A'}|${item.grade || 'N/A'}|${item.pack_size || 'N/A'}|${item.brand || 'N/A'}`;
      
      if (!summaryMap[key]) {
        summaryMap[key] = {
          product: item.product || 'N/A',
          grade: item.grade || 'N/A',
          pack_size: item.pack_size || 'N/A',
          brand: item.brand || 'N/A',
          entries: 0,
          cartons: 0,
          loose_bags: 0,
          loose_kgs: 0,
          total_kgs: 0,
          total_pounds: 0,
        };
      }
      
      summaryMap[key].entries += 1;
      summaryMap[key].cartons += Number(item.cartons) || 0;
      summaryMap[key].loose_bags += Number(item.loose_bags) || 0;
      summaryMap[key].loose_kgs += Number(item.loose_kgs) || 0;
      summaryMap[key].total_kgs += Number(item.total_kgs) || 0;
      summaryMap[key].total_pounds += Number(item.total_pounds) || 0;
    });
    
    return Object.values(summaryMap).sort((a, b) => b.cartons - a.cartons);
  };

  // Filter data based on filters
  const getFilteredData = () => {
    let filtered = data;
    
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }
    
    return filtered;
  };

  const filteredData = getFilteredData();
  const filteredTotals = calculateFilteredTotals(filteredData);
  const consolidatedSummary = getConsolidatedSummary(filteredData);

  // Get status color for Chip component
  const getStatusChipColor = (status) => {
    const colorConfig = REPACK_STATUS_COLORS[status];
    if (colorConfig) {
      return colorConfig.chipColor;
    }
    return 'warning';
  };

  // Get status background color for row highlighting
  const getStatusRowStyle = (status) => {
    const colorConfig = REPACK_STATUS_COLORS[status];
    if (colorConfig) {
      return {
        borderLeft: `4px solid ${colorConfig.background}`,
      };
    }
    return {};
  };

  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    const colorConfig = REPACK_STATUS_COLORS[status];
    if (colorConfig) {
      return {
        backgroundColor: colorConfig.background,
        color: colorConfig.color,
        padding: '4px 12px',
        borderRadius: '4px',
        fontWeight: 600,
        fontSize: '0.75rem'
      };
    }
    return {};
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format timestamp to IST (India Standard Time)
  const formatTimestampIST = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${month} ${day}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 400 }}>
            Repack-Out Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            View and manage items in REPACK-OUT, RE-PRINTING, RE-PROCESS, and ONLINE MIX status
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {user.role === 'admin' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => setShowImportModal(true)}
                sx={{ textTransform: 'none' }}
              >
                Import Data
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDelete}
                disabled={selectedRows.length === 0}
                sx={{ textTransform: 'none' }}
              >
                Delete Selected ({selectedRows.length})
              </Button>
            </>
          )}
          {/* ✅ EXPORT Button - Visible to all authenticated users */}
          <ExportButton 
            exportType="repack_out" 
            label="Export Repack-Out"
            filters={filters}
          />
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Status Summary Cards */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Status Overview</Typography>
        {filters.status && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleFilterChange('status', '')}
          >
            Clear Status Filter
          </Button>
        )}
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderLeft: `4px solid ${REPACK_STATUS_COLORS['REPACK-OUT'].background}`,
            cursor: 'pointer',
            backgroundColor: filters.status === 'REPACK-OUT' ? `${REPACK_STATUS_COLORS['REPACK-OUT'].background}10` : 'white',
            boxShadow: filters.status === 'REPACK-OUT' ? 4 : 1,
            '&:hover': { boxShadow: 3 }
          }}
          onClick={() => {
            const newStatus = filters.status === 'REPACK-OUT' ? '' : 'REPACK-OUT';
            setFilters(prev => ({ ...prev, status: newStatus }));
          }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">REPACK-OUT</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: REPACK_STATUS_COLORS['REPACK-OUT'].background }}>
                    {data.filter(item => item.status === 'REPACK-OUT').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 50, 
                  height: 50, 
                  borderRadius: '50%', 
                  backgroundColor: `${REPACK_STATUS_COLORS['REPACK-OUT'].background}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 24 }}>📦</Typography>
                </Box>
              </Box>
              {filters.status === 'REPACK-OUT' && (
                <Chip 
                  label="Active Filter" 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderLeft: `4px solid ${REPACK_STATUS_COLORS['RE-PRINTING'].background}`,
            cursor: 'pointer',
            backgroundColor: filters.status === 'RE-PRINTING' ? `${REPACK_STATUS_COLORS['RE-PRINTING'].background}10` : 'white',
            boxShadow: filters.status === 'RE-PRINTING' ? 4 : 1,
            '&:hover': { boxShadow: 3 }
          }}
          onClick={() => {
            const newStatus = filters.status === 'RE-PRINTING' ? '' : 'RE-PRINTING';
            setFilters(prev => ({ ...prev, status: newStatus }));
          }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">RE-PRINTING</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: REPACK_STATUS_COLORS['RE-PRINTING'].background }}>
                    {data.filter(item => item.status === 'RE-PRINTING').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 50, 
                  height: 50, 
                  borderRadius: '50%', 
                  backgroundColor: `${REPACK_STATUS_COLORS['RE-PRINTING'].background}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 24 }}>🖨️</Typography>
                </Box>
              </Box>
              {filters.status === 'RE-PRINTING' && (
                <Chip 
                  label="Active Filter" 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderLeft: `4px solid ${REPACK_STATUS_COLORS['RE-PROCESS'].background}`,
            cursor: 'pointer',
            backgroundColor: filters.status === 'RE-PROCESS' ? `${REPACK_STATUS_COLORS['RE-PROCESS'].background}10` : 'white',
            boxShadow: filters.status === 'RE-PROCESS' ? 4 : 1,
            '&:hover': { boxShadow: 3 }
          }}
          onClick={() => {
            const newStatus = filters.status === 'RE-PROCESS' ? '' : 'RE-PROCESS';
            setFilters(prev => ({ ...prev, status: newStatus }));
          }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">RE-PROCESS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: REPACK_STATUS_COLORS['RE-PROCESS'].background }}>
                    {data.filter(item => item.status === 'RE-PROCESS').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 50, 
                  height: 50, 
                  borderRadius: '50%', 
                  backgroundColor: `${REPACK_STATUS_COLORS['RE-PROCESS'].background}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 24 }}>🔄</Typography>
                </Box>
              </Box>
              {filters.status === 'RE-PROCESS' && (
                <Chip 
                  label="Active Filter" 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            borderLeft: `4px solid ${REPACK_STATUS_COLORS['ONLINE MIX'].background}`,
            cursor: 'pointer',
            backgroundColor: filters.status === 'ONLINE MIX' ? `${REPACK_STATUS_COLORS['ONLINE MIX'].background}10` : 'white',
            boxShadow: filters.status === 'ONLINE MIX' ? 4 : 1,
            '&:hover': { boxShadow: 3 }
          }}
          onClick={() => {
            const newStatus = filters.status === 'ONLINE MIX' ? '' : 'ONLINE MIX';
            setFilters(prev => ({ ...prev, status: newStatus }));
          }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">ONLINE MIX</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: REPACK_STATUS_COLORS['ONLINE MIX'].background }}>
                    {data.filter(item => item.status === 'ONLINE MIX').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 50, 
                  height: 50, 
                  borderRadius: '50%', 
                  backgroundColor: `${REPACK_STATUS_COLORS['ONLINE MIX'].background}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography sx={{ fontSize: 24 }}>🌐</Typography>
                </Box>
              </Box>
              {filters.status === 'ONLINE MIX' && (
                <Chip 
                  label="Active Filter" 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Color Legend */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Status Color Legend:</Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {REPACK_DASHBOARD_STATUSES.map(status => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 20, 
                height: 20, 
                borderRadius: '4px',
                backgroundColor: REPACK_STATUS_COLORS[status].background 
              }} />
              <Typography variant="body2">{status}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Totals Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Entries</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{filteredData.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Cartons</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {Number(filteredTotals.cartons || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Loose Bags</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                {Number(filteredTotals.loose_bags || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Loose KGs</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {parseFloat(filteredTotals.loose_kgs || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total KGs</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                {parseFloat(filteredTotals.total_kgs || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">Total Pounds</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                {parseFloat(filteredTotals.total_pounds || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          {/* ✅ From Date */}
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* ✅ To Date */}
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {REPACK_DASHBOARD_STATUSES.map(status => (
                  <MenuItem key={status} value={status}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '2px',
                        backgroundColor: REPACK_STATUS_COLORS[status].background 
                      }} />
                      {status}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* ✅ AUTOCOMPLETE FILTERS - Product, Grade, Brand */}
          <Grid item xs={12} md={2}>
            <Autocomplete
              options={PRODUCTS}
              value={filters.product || null}
              onChange={(e, newValue) => handleFilterChange('product', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product"
                  size="small"
                  placeholder="Type to search..."
                />
              )}
              freeSolo
              autoHighlight
              clearOnEscape
              sx={{ minWidth: 180 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              options={GRADES}
              value={filters.grade || null}
              onChange={(e, newValue) => handleFilterChange('grade', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grade"
                  size="small"
                  placeholder="Type to search..."
                />
              )}
              freeSolo
              autoHighlight
              clearOnEscape
              sx={{ minWidth: 150 }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              options={BRANDS}
              value={filters.brand || null}
              onChange={(e, newValue) => handleFilterChange('brand', newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  size="small"
                  placeholder="Type to search..."
                />
              )}
              freeSolo
              autoHighlight
              clearOnEscape
              sx={{ minWidth: 180 }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              fullWidth
              size="small"
              label="Pallet ID"
              value={filters.pallet_id}
              onChange={(e) => handleFilterChange('pallet_id', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              fullWidth
              size="small"
              label="CS1"
              value={filters.cs1}
              onChange={(e) => handleFilterChange('cs1', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField
              fullWidth
              size="small"
              label="CS2"
              value={filters.cs2}
              onChange={(e) => handleFilterChange('cs2', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ bgcolor: '#ed6c02', '&:hover': { bgcolor: '#d55b02' } }}
            >
              Apply
            </Button>
          </Grid>
          <Grid item xs={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ✅ NEW: Consolidated Summary Table - Shows when any filter is active */}
      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, border: '2px solid #ed6c02', backgroundColor: '#fff8e1' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#e65100', display: 'flex', alignItems: 'center', gap: 1 }}>
            📊 Consolidated Summary 
            <Chip label={`${consolidatedSummary.length} groups`} size="small" sx={{ backgroundColor: '#ed6c02', color: 'white' }} />
            <Chip label={`${filteredData.length} entries`} size="small" variant="outlined" />
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Pack Size</TableCell>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Brand</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Entries</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Cartons</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Loose Bags</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Loose KGs</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Total KGs</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#ed6c02', color: 'white' }}>Total LBs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidatedSummary.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fff3e0' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.product}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatGrade(row.grade)}</TableCell>
                    <TableCell>{row.pack_size}</TableCell>
                    <TableCell>{row.brand}</TableCell>
                    <TableCell align="right">{row.entries}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{row.cartons.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.loose_bags.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.loose_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>{row.total_kgs.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#ed6c02' }}>{row.total_pounds.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow sx={{ backgroundColor: '#ffe0b2' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 700 }}>GRAND TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{filteredData.length}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{filteredTotals.cartons.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{filteredTotals.loose_bags.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{filteredTotals.loose_kgs.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{filteredTotals.total_kgs.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#ed6c02' }}>{filteredTotals.total_pounds.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ✅ Section Header for Date-wise Details */}
      {isAnyFilterActive() && consolidatedSummary.length > 0 && (
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2, color: '#ed6c02', display: 'flex', alignItems: 'center', gap: 1 }}>
          📋 Date-wise Details
          <Chip label={`${filteredData.length} entries`} size="small" variant="outlined" sx={{ borderColor: '#ed6c02', color: '#ed6c02' }} />
        </Typography>
      )}

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {user.role === 'admin' && (
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 400, 
                    backgroundColor: '#ed6c02',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    borderBottom: '2px solid #d55b02',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRows.length === data.length && data.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>STATUS</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>REPACK-OUT DATE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>REPACK-OUT TIME</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PROD. DATE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>SHIFT</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PRODUCT ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>NAV ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PACK STYLE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PRODUCT</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>GRADE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PACK SIZE</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>BRAND</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>CARTONS</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>LOOSE BAGS</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>LOOSE KGS</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>LOT NO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>PALLET ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>CS1</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>CS2</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #000000', backgroundColor: '#ed6c02', position: 'sticky', top: 0, zIndex: 100, borderBottom: '2px solid #d55b02', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }} align="center">ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={user.role === 'admin' ? 20 : 19} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading repack-out inventory...</Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={user.role === 'admin' ? 20 : 19} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    No items in repack-out, re-printing, or re-process status
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow 
                  key={row.id} 
                  hover
                  sx={getStatusRowStyle(row.status)}
                >
                  {user.role === 'admin' && (
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={getStatusBadgeStyle(row.status)}>
                      {row.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <strong style={{ color: '#f57c00' }}>{formatDate(row.repack_out_date)}</strong>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#d55b02', fontWeight: 600 }}>
                      {formatTimestampIST(row.repack_out_timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {formatDate(row.production_date)}
                  </TableCell>
                  <TableCell>{row.shift}</TableCell>
                  <TableCell>{row.product_id}</TableCell>
                  <TableCell>{row.nav_id}</TableCell>
                  <TableCell>{row.pack_style}</TableCell>
                  <TableCell><strong>{row.product}</strong></TableCell>
                  <TableCell><strong>{formatGrade(row.grade)}</strong></TableCell>
                  <TableCell>{row.pack_size}</TableCell>
                  <TableCell>{row.brand}</TableCell>
                  <TableCell align="center"><strong>{row.cartons}</strong></TableCell>
                  <TableCell align="center" sx={{ color: 'error.main' }}>
                    <strong>{row.loose_bags}</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'warning.main' }}>
                    <strong>{parseFloat(row.loose_kgs).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell>{row.lot_no}</TableCell>
                  <TableCell>{row.pallet_id}</TableCell>
                  <TableCell>{row.cs1}</TableCell>
                  <TableCell>{row.cs2}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Change Status (Move back to Daily Inventory)">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setSelectedEntry(row);
                          setShowChangeStatusModal(true);
                        }}
                      >
                        <ChangeStatusIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {user.can_delete_entries && (
                      <Tooltip title="Delete Entry">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Change Status Modal */}
      <ChangeStatusModal
        open={showChangeStatusModal}
        onClose={() => {
          setShowChangeStatusModal(false);
          setSelectedEntry(null);
        }}
        onSuccess={fetchData}
        entryData={selectedEntry}
        user={user}
        restrictedStatuses={['PRODUCED', 'REPACK-IN']}
      />

      {/* Import Data Modal */}
      <ImportDataModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          fetchData();
          setShowImportModal(false);
        }}
        user={user}
        importType="repack_out"
      />
    </Box>
  );
}

export default RepackOutDashboard;